import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AuthenticatedLayout from './AuthenticatedLayout';
import { useAuthStore } from '../store/authStore';
import { useUserStore } from '../store/userStore';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useTwoFactorStore } from '../store/twoFactorStore';
import apiClient from '../lib/api/apiClient';
import LoadingPage from './LoadingPage';
import TwoFactorVerification from './TwoFactorVerification';
import { c10CacheBuster } from '../utils/cacheBusting';
import { useUser } from '../hooks/useUser';
import eventTracking from '../utils/eventTracking';

interface UserRouteProps {
  children: React.ReactNode;
}

const UserRoute = ({ children }: UserRouteProps): React.ReactElement => {
  const location = useLocation();
  const { isSignedIn, checkAuth, clearAuth } = useAuthStore();
  const { setUser, user, clearUser } = useUserStore();
  const { refetch } = useUser();
  const { setMyLeague } = useMyLeagueStore();
  const { is2FARequired, is2FAVerified, set2FARequired, clear2FA } = useTwoFactorStore();
  const [isLoading, setIsLoading] = useState(true);
  const hasInitialized = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const lastLocationRef = useRef('');

  const recordPageView = async (page: string) => {
    eventTracking(page, 'page-viewed', '', '', 0);
  }

  useEffect(() => {
    c10CacheBuster();
    if (lastLocationRef.current !== location.pathname) {
      lastLocationRef.current = location.pathname;
      if (lastLocationRef.current !== '/dashboard' && !location.search.includes('token')) {
        /*
     page,
  action,
  description = '',
  details = '',
  value = 0,
        */
        recordPageView(lastLocationRef.current);
      }
    }

  }, [location]);

  // Record deep link when user is not signed in and trying to access protected route
  useEffect(() => {
    if (!isSignedIn && !isLoading) {
      const currentPath = location.pathname + location.search;
      const existingDeepLink = localStorage.getItem('deepLinkUrl');
      console.log('Deep link check - isSignedIn:', isSignedIn, 'isLoading:', isLoading, 'path:', currentPath, 'existing:', existingDeepLink);
      
      // Only record deep link for meaningful protected routes, not defaults or auth pages
      const isProtectedRoute = currentPath !== '/signin' && 
                              currentPath !== '/signup' && 
                              currentPath !== '/dashboard' && // Don't record default dashboard
                              currentPath !== '/' && // Don't record root
                              !currentPath.startsWith('/signin') && // Don't record any signin variants
                              !currentPath.startsWith('/signup'); // Don't record any signup variants
      
      if (isProtectedRoute && !existingDeepLink) {
        console.log('Recording deep link:', currentPath);
        localStorage.setItem('deepLinkUrl', currentPath);
      } else if (!isProtectedRoute) {
        console.log('Not recording deep link for:', currentPath, '- not a protected route');
      } else {
        console.log('Deep link already exists:', existingDeepLink);
      }
    }
  }, [isSignedIn, isLoading, location.pathname, location.search]);

  // Clear deep link when user successfully reaches their intended destination
  useEffect(() => {
    if (isSignedIn && !isLoading) {
      const deepLinkUrl = localStorage.getItem('deepLinkUrl');
      const currentPath = location.pathname + location.search;
      
      console.log('Deep link cleanup check - currentPath:', currentPath, 'deepLinkUrl:', deepLinkUrl);
      
      // If we're on the deep link destination, clear it
      if (deepLinkUrl && currentPath === deepLinkUrl) {
        console.log('Reached deep link destination, clearing:', deepLinkUrl);
        localStorage.removeItem('deepLinkUrl');
      }
    }
  }, [isSignedIn, isLoading, location.pathname, location.search]);

  const forceSignOut = () => {
    clearUser();
    clearAuth();
    clear2FA();
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('email');
    // Don't clear deepLinkUrl here - we want to preserve it for after sign in
    navigate('/signin');
  }

  const handle2FAComplete = () => {
    // 2FA verification completed, user can now access the dashboard
    set2FARequired(false);
    
    // Check for deep link after 2FA completion
    const deepLinkUrl = localStorage.getItem('deepLinkUrl');
    console.log('2FA complete - checking deep link:', deepLinkUrl);
    
    if (deepLinkUrl) {
      console.log('2FA complete - redirecting to deep link:', deepLinkUrl);
      navigate(deepLinkUrl, { replace: true });
    } else {
      console.log('2FA complete - no deep link, staying on current page');
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // Check if we have search params that need processing
      const hasSearchParams = searchParams.get('token') || searchParams.get('pendingInvite');
      
      // Only skip if we've already initialized AND there are no search params to process
      if ((hasInitialized.current || isSignedIn) && user && !hasSearchParams) {
        setIsLoading(false);
        return;
      }
      
      // Reset initialization flag if we have search params to process
      if (hasSearchParams && !isSignedIn) {
        hasInitialized.current = false;
      }
      
      if (!hasInitialized.current) {
        hasInitialized.current = true;
      }

      try {
        console.log('searchParams:', searchParams);
        // First check if we have a token in the URL
        const tokenWithParams = searchParams.get('token') || localStorage.getItem('token');
        let userId = searchParams.get('userId') || tokenWithParams?.split('?')[1]?.split('=')[1];
        let email = searchParams.get('email')?.replace(" ", "+") || tokenWithParams?.split('?')[2]?.split('=')[1];
        const pendingInvite = searchParams.get('pendingInvite') || localStorage.getItem('pendingInvite');
        localStorage.setItem('pendingInvite', pendingInvite || '');
        const token = tokenWithParams?.split('?')[0];
        console.log('pendingInvite:', pendingInvite);
        if (token) {
          // Clear 2FA state before processing new user sign-in to prevent cross-account 2FA state persistence
          clear2FA();
          
          localStorage.setItem('token', token);
          if (userId) {
            localStorage.setItem('userId', userId || '');
          } else {
            userId = localStorage.getItem('userId') || '';
          }
          if (email) {
            localStorage.setItem('email', email || '');
          } else {
            email = localStorage.getItem('email') || '';
          }
          if (!userId || !email) {
            forceSignOut();
          }
          // check for pending invite
          if (pendingInvite) {
            await apiClient.post(`/team/accept-invitation/${pendingInvite}`);
            refetch();
          }

          const userResponse = await apiClient.post('/user/landing', {
            user_id: userId,
            email: email,
          });
          setUser(userResponse.data.user);

          
          // Set 2FA as required for new sign-ins
          console.log('landing info:', userResponse.data);
          // Set 2FA as required for new sign-ins
          // if user has 2fa enabled, set 2fa required to true
          // Otherwise explicitly set to false to override any persisted state from previous user
          if (userResponse.data.user.user_settings.two_fa_enabled && userResponse.data?.user?.phone) {
            set2FARequired(true);
          } else {
            set2FARequired(false);
          }
          
          
          // Fetch and set initial league data

          if (userResponse.data.user.default_league_id) {
            try {
              const leagueResponse = await apiClient.get(`/leagues/populate/${userResponse.data.user.default_league_id}`);
              setMyLeague(leagueResponse.data.league);
            } catch (error) {
              console.error('Error fetching initial league data:', error);
            }
          }

          // Remove token from URL
          setSearchParams({});
        }

        // Then check auth status
        await checkAuth();
      } catch (error) {
        console.error('Auth initialization error:', error);
        forceSignOut();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [checkAuth, setUser, searchParams, setSearchParams, setMyLeague, set2FARequired, clear2FA, location.search, refetch]);
  

  if (isLoading || (isSignedIn && !user)) {
    return <LoadingPage />;
  }

  if (!isSignedIn) {
    // The deep link is already recorded in the useEffect above
    return <Navigate to="/signin" replace />;
  }

  // Check if 2FA is required and not yet verified
  if (is2FARequired && !is2FAVerified) {
    return <TwoFactorVerification onVerificationComplete={handle2FAComplete} />;
  }

  // Handle deep link for users who don't need 2FA
  if (localStorage.getItem('deepLinkUrl')) {
    const url = localStorage.getItem('deepLinkUrl');
    console.log('Non-2FA deep link redirect to:', url);
    return <Navigate to={url || '/dashboard'} replace />;
  }

  // insure token is available for all apiClient calls
  const token = localStorage.getItem('token');
  if (!token) {
    // If no token, force sign out and redirect to signin
    forceSignOut();
    return <LoadingPage />;
  }

  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
};

export default UserRoute; 